import { format, isToday, isWithinInterval, parseISO, parse, subDays, subMonths } from 'date-fns';
import groupBy from 'lodash/groupBy';

interface ItemBase {
  created_at?: string; // ISO date string
  updated_at?: string; // ISO date string fallback
}

type GroupedItems<T extends ItemBase> = {
  today: T[];
  'last 7 days': T[];
  'last month': T[];
  [month: string]: T[];
};

const parseDate = (value?: string) => {
  if (!value) return null;
  try {
    const parsed = parseISO(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
};

const getTimestamp = <T extends ItemBase>(item: T) => {
  const date = parseDate(item.created_at || item.updated_at);
  return date ? date.getTime() : 0;
};

export function sortItemsByDateDescending<T extends ItemBase>(items: T[]) {
  return items.slice().sort((a, b) => getTimestamp(b) - getTimestamp(a));
}

export function groupItemsByCreatedAt<T extends ItemBase>(items: T[]): GroupedItems<T> {
  const today = new Date();

  const groupedItems: GroupedItems<T> = {
    today: [],
    'last 7 days': [],
    'last month': [],
  };

  const olderItems: T[] = [];

  items.forEach(item => {
    const createdAt = parseDate(item.created_at || item.updated_at) || new Date(0);

    if (isToday(createdAt)) {
      groupedItems.today.push(item);
    } else if (
      isWithinInterval(createdAt, {
        start: subDays(today, 7),
        end: today,
      })
    ) {
      groupedItems['last 7 days'].push(item);
    } else if (
      isWithinInterval(createdAt, {
        start: subMonths(today, 1),
        end: today,
      })
    ) {
      groupedItems['last month'].push(item);
    } else {
      olderItems.push(item);
    }
  });

  groupedItems.today = sortItemsByDateDescending(groupedItems.today);
  groupedItems['last 7 days'] = sortItemsByDateDescending(groupedItems['last 7 days']);
  groupedItems['last month'] = sortItemsByDateDescending(groupedItems['last month']);

  const olderGrouped = groupBy(olderItems, item => {
    const createdAt = parseDate(item.created_at || item.updated_at) || new Date(0);
    return format(createdAt, 'MMMM yyyy'); // e.g., "April 2024"
  });

  const orderedOlderGrouped = Object.keys(olderGrouped)
    .sort((a, b) => {
      const dateA = parse(a, 'MMMM yyyy', new Date()).getTime();
      const dateB = parse(b, 'MMMM yyyy', new Date()).getTime();
      return dateB - dateA;
    })
    .reduce((result: GroupedItems<T>, key) => {
      result[key] = sortItemsByDateDescending(olderGrouped[key]);
      return result;
    }, {} as GroupedItems<T>);

  return {
    ...groupedItems,
    ...orderedOlderGrouped,
  };
}
